/**
 * Web Vitals Performance Monitoring Utility
 *
 * Tracks and reports Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay) / INP (Interaction to Next Paint)
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 *
 * Note: Next.js-specific metrics (hydration, route-change-to-render, render)
 * are only available in Next.js 16+. Currently using Next.js 15.
 */

import { ddRumAction } from '@/utils/datadog';
import { datadogRum } from '@datadog/browser-rum';

// Metric type from next/web-vitals (compatible with web-vitals)
type Metric = {
    id: string;
    name: string;
    value: number;
    delta: number;
    rating?: 'good' | 'needs-improvement' | 'poor';
    navigationType?: string;
    entries?: any[];
};

type PerformanceData = {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType?: string;
};

/**
 * Determine the rating of a metric based on thresholds
 */
const getMetricRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
    const thresholds = {
        CLS: { good: 0.1, poor: 0.25 },
        FID: { good: 100, poor: 300 },
        INP: { good: 200, poor: 500 },
        LCP: { good: 2500, poor: 4000 },
        FCP: { good: 1800, poor: 3000 },
        TTFB: { good: 800, poor: 1800 }
    };

    const threshold = thresholds[name as keyof typeof thresholds];
    if (!threshold) {
        return 'good';
    }

    if (value <= threshold.good) {
        return 'good';
    }
    if (value <= threshold.poor) {
        return 'needs-improvement';
    }
    return 'poor';
};

/**
 * Format metric value for display
 */
const formatMetricValue = (name: string, value: number): string => {
    // CLS is unitless
    if (name === 'CLS') {
        return value.toFixed(3);
    }
    // Time-based metrics in milliseconds
    return `${Math.round(value)}ms`;
};

/**
 * Get color coding for console output
 */
const getRatingColor = (rating: 'good' | 'needs-improvement' | 'poor'): string => {
    switch (rating) {
        case 'good':
            return '#0cce6b'; // Green
        case 'needs-improvement':
            return '#ffa400'; // Orange
        case 'poor':
            return '#ff4e42'; // Red
        default:
            return '#000000';
    }
};

/**
 * Log metric to console with color coding (development only)
 */
export const logMetricToConsole = (metric: PerformanceData): void => {
    if (process.env.NODE_ENV !== 'production') {
        const { name, value, rating, delta, id, navigationType } = metric;
        const formattedValue = formatMetricValue(name, value);
        const color = getRatingColor(rating);

        console.group(
            `%c[Web Vitals] ⚡ ${name} ${rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌'}`,
            `color: ${color}; font-weight: bold; font-size: 14px;`
        );
        console.log(`%cValue: ${formattedValue}`, `color: ${color}; font-weight: bold;`);
        console.log(`Rating: ${rating}`);
        console.log(`Delta: ${formatMetricValue(name, delta)}`);
        console.log(`ID: ${id}`);
        if (navigationType) {
            console.log(`Navigation: ${navigationType}`);
        }
        console.groupEnd();
    }
};

/**
 * Send metric to Datadog RUM
 * Uses the centralized Datadog utility to ensure RUM is initialized
 */
export const sendToDatadog = (metric: PerformanceData): void => {
    try {
        // Only run in browser
        if (typeof window === 'undefined') {
            return;
        }

        const { name, value, rating, delta, id } = metric;

        // Use ddRumAction to ensure DD RUM is initialized
        ddRumAction(
            {
                name: `web-vital-${name.toLowerCase()}`,
                description: `${name.startsWith('Next.js') ? 'Next.js Metric' : 'Core Web Vital'}: ${name}`,
                context: {
                    metric_name: name,
                    metric_value: value,
                    metric_rating: rating,
                    metric_delta: delta,
                    metric_id: id
                },
                use_case: 'GLOBAL'
            },
            false // Don't duplicate console logs
        );

        // Also add as timing for better visualization in Datadog
        datadogRum.addTiming(`web_vital_${name.toLowerCase().replace(/\./g, '_')}`, value);

        if (process.env.NODE_ENV !== 'production') {
            console.log(`[Web Vitals] 📊 Sent ${name} to Datadog:`, { value, rating });
        }
    } catch (error) {
        console.error('[Web Vitals] Error sending metric to Datadog:', error);
    }
};

/**
 * Store metrics in localStorage for debugging
 */
export const storeMetricLocally = (metric: PerformanceData): void => {
    try {
        const key = 'web-vitals-metrics';
        const stored = localStorage.getItem(key);
        const metrics = stored ? JSON.parse(stored) : [];

        metrics.push({
            ...metric,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent
        });

        const recentMetrics = metrics.slice(-50);
        localStorage.setItem(key, JSON.stringify(recentMetrics));
    } catch (error) {
        // localStorage might be disabled
        console.warn('[Web Vitals] Could not store metric locally:', error);
    }
};

/**
 * Main handler for Web Vitals metrics
 */
export const reportWebVitals = (metric: Metric): void => {
    const performanceData: PerformanceData = {
        name: metric.name,
        value: metric.value,
        rating: getMetricRating(metric.name, metric.value),
        delta: metric.delta,
        id: metric.id,
        navigationType: metric.navigationType
    };

    // Log to console in development
    if (process.env.NODE_ENV !== 'production') {
        logMetricToConsole(performanceData);
        storeMetricLocally(performanceData);
    }

    // Send to Datadog RUM
    sendToDatadog(performanceData);
};

/**
 * Get all stored metrics from localStorage
 */
export const getStoredMetrics = (): any[] => {
    try {
        const stored = localStorage.getItem('web-vitals-metrics');
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('[Web Vitals] Error reading stored metrics:', error);
    }
    return [];
};

/**
 * Clear stored metrics
 */
export const clearStoredMetrics = (): void => {
    try {
        localStorage.removeItem('web-vitals-metrics');
        console.log('[Web Vitals] ✅ Cleared stored metrics');
    } catch (error) {
        console.error('[Web Vitals] Error clearing stored metrics:', error);
    }
};

/**
 * Get performance summary
 */
export const getPerformanceSummary = (): Record<string, any> => {
    const metrics = getStoredMetrics();
    if (metrics.length === 0) {
        return {};
    }

    const summary: Record<string, any> = {};
    const metricNames = ['CLS', 'FID', 'INP', 'LCP', 'FCP', 'TTFB'];

    metricNames.forEach((name) => {
        const metricData = metrics.filter((m) => m.name === name);
        if (metricData.length > 0) {
            const values = metricData.map((m) => m.value);
            summary[name] = {
                count: values.length,
                min: Math.min(...values),
                max: Math.max(...values),
                avg: values.reduce((a, b) => a + b, 0) / values.length,
                latest: metricData[metricData.length - 1]
            };
        }
    });

    return summary;
};

/**
 * Get performance summary with all available metrics
 * Note: In Next.js 15, only Core Web Vitals are available.
 * Next.js-specific metrics require Next.js 16+.
 */
export const getPerformanceSummaryWithAllMetrics = (): Record<string, any> => {
    const metrics = getStoredMetrics();
    if (metrics.length === 0) {
        return {};
    }

    const summary: Record<string, any> = {};

    const allMetricNames = Array.from(new Set(metrics.map((m) => m.name)));

    allMetricNames.forEach((name) => {
        const metricData = metrics.filter((m) => m.name === name);
        if (metricData.length > 0) {
            const values = metricData.map((m) => m.value);
            summary[name] = {
                count: values.length,
                min: Math.min(...values),
                max: Math.max(...values),
                avg: values.reduce((a, b) => a + b, 0) / values.length,
                latest: metricData[metricData.length - 1]
            };
        }
    });

    return summary;
};
